<script setup>
import { useVuelidate } from '@vuelidate/core';
import { minLength, required } from '@vuelidate/validators';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import { computed, onMounted, reactive } from 'vue';
import { useI18n } from 'vue-i18n';

import ComboBox from 'next/combobox/ComboBox.vue';
import Input from 'next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';

const emit = defineEmits(['submit']);
const getters = useStoreGetters();
const store = useStore();

const { t } = useI18n();
const { toLocale } = useAutomationLocales();

const templates = getters['wgptAutomationRegistry/getTemplates'];
const uiFlags = getters['wgptAutomationRegistry/getUIFlags'];

const initialState = {
  name: '',
  description: '',
  template: '',
};

const state = reactive({ ...initialState });

const rules = {
  name: { required, minLength: minLength(1) },
  description: {},
  template: {},
};

const v$ = useVuelidate(rules, state);

const resetState = () => {
  Object.assign(state, initialState);
};

const prepareAutomationDetails = () => ({
  name: state.name,
  description: state.description,
  template: state.template,
});

const validate = () => {
  return v$.value.$validate();
};

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: toLocale(item[labelKey]),
  })) ?? [];

const templateOptions = computed(() =>
  mapToOptions(templates.value, 'id', 'name_label')
);

const handleSubmit = async () => {
  const isFormValid = await validate();
  if (!isFormValid) return;

  emit('submit', prepareAutomationDetails());
};

const fetchTemplates = () => {
  if (templates.value) return;

  store.dispatch('wgptAutomationRegistry/getTemplates');
};

onMounted(() => {
  fetchTemplates();
});

defineExpose({ state, validate, resetState });
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.name"
      :label="t('WGPT_AUTOMATIONS.ADD.FORM.NAME.LABEL')"
    />

    <TextArea
      v-model="state.description"
      :label="t('WGPT_AUTOMATIONS.ADD.FORM.DESCRIPTION.LABEL')"
    />

    <div class="flex flex-col gap-1 hidden">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('WGPT_AUTOMATIONS.ADD.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.template"
        :disabled="uiFlags.isFetchingTemplates"
        :options="templateOptions"
        :placeholder="t('WGPT_AUTOMATIONS.ADD.FORM.TEMPLATE.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>
  </form>
</template>
